import React, {useCallback, useEffect, useState} from "react";
import {Box, Card, CardActionArea, CardContent, Grid} from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import {makeStyles} from "@material-ui/core/styles";
import {HorizontalBar} from "react-chartjs-2";
import {useHistory} from "react-router-dom";
import AuthProvider from "@user-provider/AuthProvider";
import PrescriptionService from "@user-service/PrescriptionService";

/**
 * 기초활동 섹션 - 기초율동 + 좌우연결
 *
 * 좌측: 기초율동 — 폐안 구간 알파 대역 피크Hz, 평균mV, 상대파워
 * 우측: 좌우연결 — 상관성/일관성 바차트 (더미 데이터, 추후 계산식 적용)
 */
export default function CurrentBasicRhythm({measureResult}) {
    const classes = useStyles();
    const history = useHistory();
    const {id, isEEG} = measureResult;
    const [rhythmData, setRhythmData] = useState(null);
    const [connectivityData, setConnectivityData] = useState(null);

    const fetchRhythm = useCallback(async () => {
        try {
            const data = await PrescriptionService.getBasicRhythm(id);
            if (data) {
                setRhythmData(data);
            }
        } catch (e) {}
    }, [id]);

    const fetchConnectivity = useCallback(async () => {
        try {
            const data = await PrescriptionService.getConnectivity(id);
            if (data) {
                setConnectivityData(data);
            }
        } catch (e) {}
    }, [id]);

    useEffect(() => {
        if ('yes' === isEEG) {
            fetchRhythm().then();
            fetchConnectivity().then();
        }
    }, [fetchRhythm, fetchConnectivity, isEEG]);

    const ch1 = rhythmData ? rhythmData.ch1 : null;
    const ch2 = rhythmData ? rhythmData.ch2 : null;

    return (
        <Grid container spacing={2}>
            {/* 왼쪽: 기초율동 */}
            <Grid item xs={6}>
                <Card className={classes.card} onClick={() => {
                    if (AuthProvider.canViewMeasureDetail()) {
                        history.push('/measureDetail/' + id + '/basicRhythm');
                    }
                }}>
                    <CardActionArea>
                        <CardContent className={classes.cardContent}>
                            <Typography variant={'subtitle1'} className={classes.title}>
                                기초율동
                            </Typography>
                            <Box mt={2}>
                                <RhythmRow
                                    label="좌뇌"
                                    color="rgba(0,178,245,1)"
                                    bgColor="rgba(0,178,245,0.08)"
                                    peakHz={ch1 ? ch1.peakHz : '-'}
                                    peakMv={ch1 ? ch1.peakMv : '-'}
                                    relativePower={ch1 ? ch1.relativePower : '-'}
                                />
                                <Box>
                                    <RhythmRow
                                        label="우뇌"
                                        color="rgba(199,116,210,1)"
                                        bgColor="rgba(199,116,210,0.08)"
                                        peakHz={ch2 ? ch2.peakHz : '-'}
                                        peakMv={ch2 ? ch2.peakMv : '-'}
                                        relativePower={ch2 ? ch2.relativePower : '-'}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </CardActionArea>
                </Card>
            </Grid>

            {/* 오른쪽: 좌우연결 */}
            <Grid item xs={6}>
                <Card className={classes.card} onClick={() => {
                    if (AuthProvider.canViewMeasureDetail()) {
                        history.push('/measureDetail/' + id + '/basicRhythm');
                    }
                }}>
                    <CardActionArea>
                        <CardContent className={classes.cardContent}>
                            <Typography variant={'subtitle1'} className={classes.title}>
                              뇌기능 (상관 / 일관)
                            </Typography>
                            <Box mt={2}>
                                <ConnectivityChart
                                    correlation={connectivityData ? connectivityData.correlation : 0}
                                    coherence={connectivityData ? connectivityData.coherence : 0}
                                />
                            </Box>
                        </CardContent>
                    </CardActionArea>
                </Card>
            </Grid>
        </Grid>
    );
}

/**
 * 기초율동 1줄 행 — 피크Hz | 평균mV | 비중
 */
function RhythmRow({label, color, bgColor, peakHz, peakMv, relativePower}) {
    const classes = useStyles();

    const powerPercent = typeof relativePower === 'number'
        ? (relativePower * 100).toFixed(1)
        : '-';

    return (
        <Box
            className={classes.rhythmRow}
            style={{borderLeft: `3px solid ${color}`, backgroundColor: bgColor}}
        >
            <Grid container alignItems="center">
                <Grid item xs={2}>
                    <Typography className={classes.rhythmLabel}>
                        {label}
                    </Typography>
                </Grid>
                <Grid item xs={3}>
                    <Box textAlign="center">
                        <Typography className={classes.rhythmValue}>
                            {peakHz !== '-' ? `${peakHz} Hz` : '-'}
                        </Typography>
                        <Typography className={classes.rhythmCaption}>
                            피크 주파수
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={1}>
                    <Box textAlign="center" className={classes.divider}>|</Box>
                </Grid>
                <Grid item xs={3}>
                    <Box textAlign="center">
                        <Typography className={classes.rhythmValue}>
                            {peakMv !== '-' ? `${peakMv} μV` : '-'}
                        </Typography>
                        <Typography className={classes.rhythmCaption}>
                            평균값
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={1}>
                    <Box textAlign="center" className={classes.divider}>|</Box>
                </Grid>
                <Grid item xs={2}>
                    <Box textAlign="center">
                        <Typography className={classes.rhythmValue}>
                            {powerPercent !== '-' ? `${powerPercent}%` : '-'}
                        </Typography>
                        <Typography className={classes.rhythmCaption}>
                            비중
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

/**
 * 좌우연결 바차트 — 상관성(Correlation), 일관성(Coherence)
 * Chart.js 2.x HorizontalBar
 * 상관성: 시간 영역 피어슨 상관계수 (0~100)
 * 일관성: 주파수 영역 교차 스펙트럼 Coherence (0~100)
 */
function ConnectivityChart({correlation, coherence}) {
    const chartData = {
        labels: ['상관성', '일관성'],
        datasets: [{
            data: [correlation, coherence],
            backgroundColor: ['rgba(75,192,192,0.6)', 'rgba(153,102,255,0.6)'],
            borderColor: ['rgba(75,192,192,1)', 'rgba(153,102,255,1)'],
            borderWidth: 1,
            barPercentage: 0.5,
            categoryPercentage: 0.8,
        }]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: false,
        },
        tooltips: {
            backgroundColor: 'rgba(0,0,0,0.7)',
            titleFontSize: 12,
            bodyFontSize: 11,
            callbacks: {
                label: function (tooltipItem) {
                    return `${tooltipItem.value}`;
                }
            }
        },
        scales: {
            xAxes: [{
                display: true,
                gridLines: {
                    display: true,
                    color: 'rgba(0,0,0,0.05)',
                },
                ticks: {
                    fontColor: '#666',
                    fontSize: 11,
                    min: 0,
                    max: 100,
                    stepSize: 25,
                },
                scaleLabel: {
                    display: false,
                }
            }],
            yAxes: [{
                display: true,
                barPercentage: 0.5,
                categoryPercentage: 0.8,
                gridLines: {
                    display: false,
                },
                ticks: {
                    fontColor: '#555',
                    fontSize: 12,
                    fontStyle: 'bold',
                }
            }]
        }
    };

    return (
        <Box style={{height: 100, position: 'relative'}}>
            <HorizontalBar
                key={Math.random()}
                data={chartData}
                options={options}
            />
        </Box>
    );
}

const useStyles = makeStyles(() => ({
    card: {
        border: "none",
        boxShadow: "none",
        padding: 0,
    },
    cardContent: {
        padding: '8px 16px',
    },
    title: {
        fontWeight: 'bold',
        paddingLeft: 4,
        marginBottom: 0,
        fontSize: 15,
    },
    rhythmRow: {
        borderRadius: 4,
        padding: '8px 6px',
    },
    rhythmLabel: {
        fontWeight: 'bold',
        fontSize: 13,
        textAlign: 'center',
    },
    rhythmValue: {
        fontWeight: 'bold',
        fontSize: 14,
        color: '#333',
    },
    rhythmCaption: {
        fontSize: 10,
        color: '#999',
        marginTop: 1,
    },
    divider: {
        color: '#ddd',
        fontSize: 18,
        fontWeight: 300,
    },
}));
